import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { Container, Spinner } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { datadogLogger } from '@telialt/utils/src/datadog/datadogLogger';
import { DDComponent } from '@telialt/utils/src/datadog/setup';

import { BlockSection } from '../../../common/Common.styles';
import { CoverageMapContainer, CoverageMapIframe, CoverageMapIframeLoader } from './CoverageMap.styles';
import { getCoverageMapUrl } from './utils';

const LOADING_TIMEOUT = 5000;

export const CoverageMap: FC = () => {
    const { t } = useTranslations();

    const [isLoaded, setIsLoaded] = useState(false);

    const isLoadedRef = useRef<boolean>(false);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        isLoadedRef.current = false;

        timerRef.current = window.setTimeout(() => {
            if (!isLoadedRef.current) {
                const datadogLogs = datadogLogger();
                datadogLogs.warn('Coverage map iframe loading timed out', { component: DDComponent.CoverageMap });
            }
        }, LOADING_TIMEOUT);

        return () => {
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    const handleLoad = () => {
        isLoadedRef.current = true;
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setIsLoaded(true);
    };

    const handleError = () => {
        const datadogLogs = datadogLogger();
        datadogLogs.warn('Coverage map iframe loading failed', { component: DDComponent.CoverageMap });
    };

    // When iframe is rendered in server side, onLoad event does not fire. Setting src dynamically to force onLoad to fire.
    const iframeNodeHandler = useCallback((iframe: HTMLIFrameElement) => {
        if (!iframe) {
            return;
        }
        iframe.src = getCoverageMapUrl();
    }, []);

    return (
        <Container>
            <BlockSection>
                <CoverageMapContainer>
                    <CoverageMapIframe
                        title={t['content.block_widget.coverage_map.title']}
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        onLoad={handleLoad}
                        onError={handleError}
                        ref={iframeNodeHandler}
                    />
                    <CoverageMapIframeLoader $isVisible={!isLoaded}>
                        <Spinner size="lg" />
                    </CoverageMapIframeLoader>
                </CoverageMapContainer>
            </BlockSection>
        </Container>
    );
};
